import type {
  ColumnPinning,
  ColumnVisibilityModel,
  Density,
  FilterModel,
  SortModel,
} from "./types";

// Bump when saved columns change meaning; older visibility, order, widths and pinning are dropped.
export const LAYOUT_VERSION = 3;

// Subset of MUI's exported grid state, so layouts saved by the old grid still load.
export interface PersistedTableState {
  layoutVersion?: number;
  columns?: {
    columnVisibilityModel?: ColumnVisibilityModel;
    orderedFields?: string[];
    dimensions?: Record<string, { width?: number; flex?: number }>;
  };
  pinning?: ColumnPinning;
  filter?: { filterModel?: FilterModel };
  sorting?: { sortModel?: SortModel };
  density?: Density;
}

export function loadTableState(key: string): PersistedTableState | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as PersistedTableState) : undefined;
  } catch {
    return undefined;
  }
}

export function saveTableState(key: string, state: PersistedTableState): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Storage full or blocked: the table keeps working, layout just isn't saved.
  }
}

// Same key and shape as the MUI grid's per-tab store, so saved choices carry over.
function tabVisibilityKey(key: string): string {
  return `${key}_tabColumns`;
}

export function loadTabVisibility(
  key: string,
  tab: string,
): ColumnVisibilityModel | undefined {
  try {
    const raw = window.localStorage.getItem(tabVisibilityKey(key));
    return raw
      ? (JSON.parse(raw) as Record<string, ColumnVisibilityModel>)[tab]
      : undefined;
  } catch {
    return undefined;
  }
}

export function saveTabVisibility(
  key: string,
  tab: string,
  model: ColumnVisibilityModel,
): void {
  try {
    const raw = window.localStorage.getItem(tabVisibilityKey(key));
    const all = raw
      ? (JSON.parse(raw) as Record<string, ColumnVisibilityModel>)
      : {};
    all[tab] = model;
    window.localStorage.setItem(tabVisibilityKey(key), JSON.stringify(all));
  } catch {
    // Storage full or blocked: the choice just isn't saved.
  }
}

export function savedUserWidths(
  saved: PersistedTableState | undefined,
): Record<string, number> {
  if (saved?.layoutVersion !== LAYOUT_VERSION) return {};
  const widths: Record<string, number> = {};
  Object.entries(saved.columns?.dimensions ?? {}).forEach(([field, dim]) => {
    if (dim.width !== undefined) widths[field] = dim.width;
  });
  return widths;
}
